import React from "react";
import PostedByAgentLine from "../club/PostedByAgentLine";
import {
  formatRecruitingLevel,
  postedByMemberOf,
  recruitingLevelPillClass,
} from "../../utils/tenderFormatters";

/**
 * The page's own header. It begins the page — there is no app navigation above
 * it on this surface.
 *
 * Three things changed against V1 and all three were measured (inventory 5.6,
 * 7.4, 7.5):
 *   * the back arrow was a 20x20 tap target; it is 44x44 now, like every other
 *     control on this surface.
 *   * the row carried `px-12` (48px a side), which ate 96 of 375px. Padding now
 *     comes from the page column, which clamps between 16px and 48px.
 *   * "Export to Excel" is GONE. Marin ruled the export was never intended in
 *     this area (build brief: "the button exists in the old code; Marin ruled it
 *     was never intended. Remove it and its handler"), so the button, its
 *     spinner state and `handleExportToExcel` in the hook are all removed rather
 *     than hidden.
 */
const ViewPitchesHeader = ({
  tender,
  formatPosition,
  navigate,
  setShowTenderDetails,
}) => {
  // The level the request recruits for, on the position line — the one place
  // the prototype states it without opening a dialog (it writes the youth
  // request's position as "Center Forward (U19)"). The pill is the platform's
  // own for this field, and nothing is drawn when the request has no level.
  const recruitingLevel = formatRecruitingLevel(tender?.recruiting_for);
  // Who at the club wrote this request. Several people at one club have access,
  // and the author is the colleague to ask about it. The line falls back to
  // nothing when the payload names nobody; the agency case still wins inside
  // PostedByAgentLine when an agency posted the request for the club.
  const postedByMember = postedByMemberOf(tender);

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label="Back to Requests"
        title="Back to Requests"
        className="-ml-2.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] text-[#374151] hover:bg-[#E9EEF6]"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <h1 className="m-0 text-[22px] font-extrabold text-[#111827]">View Pitches</h1>
      <span className="mx-1.5 h-[22px] w-[2px] bg-[#E5E7EB]" aria-hidden="true" />
      <span className="min-w-0 text-[18px] font-medium text-[#374151]">
        {formatPosition(tender?.position)}
      </span>
      {recruitingLevel ? (
        <span
          className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${recruitingLevelPillClass(
            tender?.recruiting_for,
          )}`}
        >
          {recruitingLevel}
        </span>
      ) : null}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowTenderDetails(true)}
          className="min-h-[44px] whitespace-nowrap rounded-[10px] border border-[#BCD8FE] bg-white px-[18px] text-[14px] font-semibold text-[#2183FE] hover:bg-[#EFF6FF]"
        >
          View Request details
        </button>
      </div>

      {/* Who wrote this request. DECISION 009 — if an AGENCY posted it for the
          club, say so, or the club answers pitches on a request it never wrote.
          Otherwise the colleague who posted it, from `tender.created_by`, which
          the same endpoint has always sent and this page never read.
          `basis-full` keeps the line on its own row so it can never be squeezed
          by the title. */}
      <PostedByAgentLine
        attribution={tender?.agent_attribution}
        name={postedByMember?.name}
        org={postedByMember?.org}
        className="basis-full"
      />
    </div>
  );
};

export default ViewPitchesHeader;
